import { Ref, CSSProperties, useCallback } from "react";

import { Flex, Text } from "@radix-ui/themes";
import { Control, Controller, FieldValues, Path } from "react-hook-form";
import {
  MultiValueGenericProps,
  OptionProps,
  SingleValueProps,
  components,
} from "react-select";
import ReactSelect, {
  SingleValue,
  MultiValue,
  Props as BaseProps,
  GroupBase,
  PlaceholderProps,
} from "react-select";
import SelectDecl from "react-select/dist/declarations/src/Select";

import { SelectOption } from "./interface";
import { Tag } from "../Tag";

export interface BasicSelectProps<Option = SelectOption> {
  options: Option[];
  value: MultiValue<Option> | SingleValue<Option>;
  isMulti: boolean;
  isLoading?: boolean;
  onChange: (value: MultiValue<Option> | SingleValue<Option>) => void;
  style?: CSSProperties;
}

export type CoreProps<
  Option = SelectOption,
  Group extends GroupBase<Option> = GroupBase<Option>,
  IsMulti extends boolean = false
> = BaseProps<Option, IsMulti, Group> & {
  error?: boolean;
  innerRef?: Ref<SelectDecl<Option, IsMulti, Group>>;
};

const CustomOption = <
  Option = SelectOption,
  Group extends GroupBase<Option> = GroupBase<Option>,
  IsMulti extends boolean = false
>({
  children,
  ...props
}: OptionProps<Option, IsMulti, Group>) => {
  return (
    <components.Option {...props}>
      <Text size="2">{children}</Text>
    </components.Option>
  );
};

const CustomSingleValue = <
  Option = SelectOption,
  Group extends GroupBase<Option> = GroupBase<Option>,
  IsMulti extends boolean = false
>({
  children,
  ...props
}: SingleValueProps<Option, IsMulti, Group>) => {
  return (
    <components.SingleValue {...props}>
      <Text size="2">{children}</Text>
    </components.SingleValue>
  );
};

const CustomPlaceholder = <
  Option = SelectOption,
  Group extends GroupBase<Option> = GroupBase<Option>,
  IsMulti extends boolean = false
>({
  children,
  ...props
}: PlaceholderProps<Option, IsMulti, Group>) => {
  return (
    <components.Placeholder {...props}>
      <Text size="2">{children}</Text>
    </components.Placeholder>
  );
};

const CustomMultiValueContainer = <
  Option = SelectOption,
  Group extends GroupBase<Option> = GroupBase<Option>,
  IsMulti extends boolean = false
>({
  children,
  ...props
}: // eslint-disable-next-line @typescript-eslint/no-explicit-any
MultiValueGenericProps<Option, IsMulti, Group>) => {
  return (
    <components.MultiValueContainer {...props}>
      <Tag>{children}</Tag>
    </components.MultiValueContainer>
  );
};

export const Select = <
  Option = SelectOption,
  Group extends GroupBase<Option> = GroupBase<Option>,
  IsMulti extends boolean = false
>({
  options,
  value,
  isMulti,
  isLoading,
  onChange,
  ...props
}: BasicSelectProps<Option> & CoreProps<Option, Group, IsMulti>) => {
  return (
    <ReactSelect
      options={options}
      isMulti={isMulti}
      value={value}
      isLoading={isLoading}
      onChange={onChange}
      maxMenuHeight={200}
      components={{
        Option: CustomOption,
        SingleValue: CustomSingleValue,
        Placeholder: CustomPlaceholder,
        MultiValueContainer: CustomMultiValueContainer,
        ...props.components,
      }}
      {...props}
      ref={props.innerRef}
    />
  );
};

type SelectWithControllerProps<
  FormValues extends FieldValues,
  Option = SelectOption
> = {
  control: Control<FormValues>;
  name: Path<FormValues>;
  required?: boolean;
  placeholder?: string;
  options: Option[];
  isMulti: boolean;
  isLoading?: boolean;
  style?: CSSProperties;
};

export const SelectWithController = <
  Option = SelectOption,
  Group extends GroupBase<Option> = GroupBase<Option>,
  IsMulti extends boolean = false,
  FormValues extends FieldValues = FieldValues
>({
  control,
  name,
  required,
  placeholder,
  options,
  isMulti,
  isLoading,
  menuPosition,
  style,
  ...props
}: CoreProps<Option, Group, IsMulti> &
  SelectWithControllerProps<FormValues>) => {
  const resolveValue = useCallback(
    (value: string | string[]) => {
      if (isMulti && Array.isArray(value)) {
        return options.filter((option: SelectOption) =>
          value?.includes(option.value)
        );
      }
      return options.find((option: SelectOption) => option.value === value);
    },
    [isMulti, options]
  );

  return (
    <Controller
      name={name}
      control={control}
      rules={{ required }}
      render={({ field: { ref, value, onChange, ...field }, fieldState }) => (
        <Select
          {...field}
          menuPlacement="auto"
          options={options}
          placeholder={placeholder}
          value={resolveValue(value)}
          innerRef={ref}
          error={!!fieldState.error}
          isMulti={isMulti}
          isLoading={isLoading}
          menuPosition={menuPosition}
          onChange={(value) =>
            isMulti
              ? onChange(
                  ((value ?? []) as MultiValue<SelectOption>).map(
                    (v) => v.value
                  )
                )
              : onChange(
                  value ? (value as SingleValue<SelectOption>)?.value : null
                )
          }
          style={style}
          components={{
            IndicatorSeparator: null,
            ClearIndicator: () => null,
            MultiValueContainer: CustomMultiValueContainer,
            MultiValueLabel: CustomMultiValueLabel,
          }}
          closeMenuOnSelect={isMulti ? false : true}
          styles={{
            container: (provided) => ({
              ...provided,
              width: "100%",
            }),
            control: (provided) => ({
              ...provided,
              cursor: "pointer",
            }),
            input: (provided) => ({
              ...provided,
              width: "0px",
              margin: "0",
              marginLeft: "var(--space-1)",
            }),
            placeholder: (provided, props) => ({
              ...provided,
              color: "gray",
              fontSize: "var(--font-size-2)",
              display: props.isFocused ? "none" : "block",
            }),
            multiValueRemove: (provided) => ({
              ...provided,
              fontSize: "var(--font-size-1)",
              color: "var(--gray-11)",
              padding: "0",
              cursor: "pointer",
              "&:hover": {
                color: "var(--gray-10)",
                backgroundColor: "transparent",
              },
            }),
            singleValue: (provided) => ({
              ...provided,
              fontSize: "var(--font-size-2)",
              color: "var(--gray-12)",
            }),
          }}
        />
      )}
    />
  );
};

const CustomMultiValueLabel = <
  Option = SelectOption,
  Group extends GroupBase<Option> = GroupBase<Option>,
  IsMulti extends boolean = false
>({
  children,
}: MultiValueGenericProps<Option, IsMulti, Group>) => {
  return <Text>{children}</Text>;
};

export const CustomTagSelect = ({
  children,
  color = "accent",
}: {
  children: React.ReactNode;
  color: string;
}) => {
  return (
    <Flex
      style={{
        cursor: "pointer",
        width: "fit-content",
        backgroundColor: `var(--${color}-a3)`,
        fontSize: "var(--font-size-1)",
        borderRadius: "var(--radius-1)",
        fontWeight: "var(--font-weight-medium)",
        padding: "calc(var(--space-1) * 0.5) calc(var(--space-1) * 1.5)",
      }}
    >
      {children}
    </Flex>
  );
};
